import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

import GroupPageShell from "../components/common/GroupPageShell";
import DivisionIntro from "../components/common/DivisionIntro";
import DivisionContact from "../components/common/DivisionContact";
import DivisionClients from "../components/common/DivisionClients";
import DivisionClientsStrip from "../components/common/DivisionClientsStrip";
import { buildClientLogoImages } from "../utils/clientLogos";
import DivisionServices from "../components/common/DivisionServices";
import DivisionBranches from "../components/common/DivisionBranches";

const HamdanTravel: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();

  const activeTab = searchParams.get("tab") || "about";

  useEffect(() => {
    fetch("/api/homepage/content")
      .then(r => r.json())
      .then(data => {
        setContent(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const menuItems = [
    { id: "about", label: t('common.about') },
    { id: "services", label: t('common.services') },
    { id: "branches", label: t('common.branches') },
    { id: "clients", label: t('common.clients') },
    { id: "contact", label: t('common.contact') },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;

  const travelData = content?.Travel || {};
  const clientLogoImages = buildClientLogoImages(travelData);
  const clientLogoUrls = t_api(travelData.clients_logo_urls, travelData.clients_logo_urls_ar);

  // Extract travel branches br_1 .. br_40
  const travelBranches = Array.from({ length: 40 }, (_, i) => i + 1)
    .reduce<Array<{ name: string; address?: string; phone?: string; fax?: string; email?: string; coordinates?: string }>>((acc, n) => {
      const name = t_api(travelData[`br_${n}_name`], travelData[`br_${n}_name_ar`]);
      if (name) {
        acc.push({
          name,
          address: t_api(travelData[`br_${n}_addr`], travelData[`br_${n}_addr_ar`]),
          phone: travelData[`br_${n}_phone`],
          fax: travelData[`br_${n}_fax`],
          email: travelData[`br_${n}_email`],
          coordinates: travelData[`br_${n}_coord`],
        });
      }
      return acc;
    }, []);

  // Build banner content
  const bannerContent = {
    title: t_api(travelData.banner_title, travelData.banner_title_ar),
    subtitle: t_api(travelData.banner_subtitle, travelData.banner_subtitle_ar),
    banners: [
      { image: travelData.banner_1 || "/banner/travel_1.jpg" },
      { image: travelData.banner_2 || "/banner/travel_2.jpg" },
      { image: travelData.banner_3 || "/banner/travel_3.jpg" },
    ],
  };

  // Build intro content
  const introContent = {
    kicker: "Hamdan Group - Oman",
    title: t_api(travelData.about_title, travelData.about_title_ar),
    desc: t_api(travelData.about_desc, travelData.about_desc_ar),
    image: travelData.about_image || "/Hamdan-Travel-logo.jpg",
    imageFit: "contain",
    imageBadgeTitle: "Travel & Tourism",
    imageLabel: t_api(travelData.about_title, travelData.about_title_ar) || "Hamdan Travel & Tourism",
    noteTitle: "Services",
    noteDesc: "Air ticketing, hotel bookings, car rental, and tour operations.",
    panelTitle: "INTRODUCTION",
    capabilitiesTitle: "Travel Capabilities",
    capabilitiesMeta: "IATA - Tours - Ticketing",
    brandLogo: "/Hamdan-Travel-logo.jpg",
    brandDesc: "A travel and tourism agency supporting individuals, ministries, and corporate travel requirements.",
    brandTags: ["Travel", "Tourism", "Ticketing"],
    points: [1, 2, 3].map(id => ({
      title: t_api(travelData[`about_pt_${id}_title`], travelData[`about_pt_${id}_title_ar`]),
      desc: t_api(travelData[`about_pt_${id}_desc`], travelData[`about_pt_${id}_desc_ar`]),
      icon: travelData[`about_pt_${id}_icon`],
    })),
  };

  // Build contact content
  const contactContent = {
    info_desc: t_api(travelData.contact_info_desc, travelData.contact_info_desc_ar),
    address: t_api(travelData.contact_address, travelData.contact_address_ar),
    phone: travelData.contact_phone,
    fax: travelData.contact_fax,
    support_phone: travelData.contact_support_phone,
    email: travelData.contact_email,
  };

  const defaultContact = {
    location: "Salalah Sultanate of Oman",
    address: contactContent.address,
    phone: contactContent.phone,
    fax: contactContent.fax,
    email: contactContent.email,
  };

  const travelContactList = travelBranches.length
    ? travelBranches.map(b => ({
        location: b.name,
        address: b.address,
        phone: b.phone,
        fax: b.fax,
        email: b.email,
      }))
    : [defaultContact];

  return (
    <GroupPageShell
      heroContent={bannerContent}
      tabs={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId) => setSearchParams({ tab: tabId })}
    >
      <div className="mt-8">
        {activeTab === "about" && (
          <>
            <DivisionIntro content={introContent} />
            <DivisionClientsStrip
              title={t_api(travelData.clients_title, travelData.clients_title_ar)}
              desc={t_api(travelData.clients_desc, travelData.clients_desc_ar)}
              clientsText={t_api(travelData.clients_list, travelData.clients_list_ar)}
              logoUrls={clientLogoUrls}
              logoImages={clientLogoImages}
            />
          </>
        )}
        {activeTab === "services" && (
          <DivisionServices content={travelData} />
        )}
        {activeTab === "branches" && (
          <DivisionBranches
            divisionLabel="Hamdan Travel & Tourism"
            title="Branch Network"
            subtitle="Our travel agency branch locations and customer service offices across Oman."
            branches={travelBranches}
          />
        )}
        {activeTab === "clients" && <DivisionClients content={{
          title: t_api(travelData.clients_title, travelData.clients_title_ar),
          desc: t_api(travelData.clients_desc, travelData.clients_desc_ar),
          clientsText: t_api(travelData.clients_list, travelData.clients_list_ar),
          logoUrls: clientLogoUrls,
          logoImages: clientLogoImages,
          divisionLabel: "Hamdan Tour And Travel",
          footer: t_api(travelData.clients_footer, travelData.clients_footer_ar),
          footerDesc: t_api(travelData.clients_footer_desc, travelData.clients_footer_desc_ar),
        }} />}
        {activeTab === "contact" && <DivisionContact content={{
          title: t_api(travelData.contact_form_title, travelData.contact_form_title_ar),
          infoTitle: t_api(travelData.contact_info_title, travelData.contact_info_title_ar),
          infoDesc: contactContent.info_desc,
          division: "Travel",
          contactInfo: travelContactList,
          supportInfo: contactContent.support_phone ? [{ label: "Telephone", value: contactContent.support_phone }] : [],
          supportEmail: contactContent.email,
        }} />}
      </div>
    </GroupPageShell>
  );
};

export default HamdanTravel;
